import React, { createContext, useContext, useReducer, useCallback, type ReactNode } from 'react';
import { toast } from 'react-hot-toast';
import type { CoverLetterContent, CoverLetterVersion } from '../models/cover-letter';
import { coverLetterService } from '../services/coverLetterService';

// --- Types ---
interface CoverLetterEditorState {
    coverLetterId: string | null;
    currentVersion: CoverLetterVersion | null;
    history: {
        past: CoverLetterVersion[];
        future: CoverLetterVersion[];
    };
    isDirty: boolean;
    isGenerating: boolean;
    canvasRef: React.RefObject<HTMLDivElement | null>;
}

// --- Actions ---
type Action =
    | { type: 'LOAD_VERSION'; payload: { version: CoverLetterVersion; coverLetterId: string } }
    | { type: 'UPDATE_LOCAL'; payload: { content: CoverLetterContent } }
    | { type: 'UNDO' }
    | { type: 'REDO' }
    | { type: 'GENERATE_START' }
    | { type: 'GENERATE_SUCCESS'; payload: { version: CoverLetterVersion } }
    | { type: 'GENERATE_ERROR' };

// --- State ---
const INITIAL_STATE: CoverLetterEditorState = {
    coverLetterId: null,
    currentVersion: null,
    history: { past: [], future: [] },
    isDirty: false,
    isGenerating: false,
    canvasRef: { current: null }
};

// --- Reducer ---
function reducer(state: CoverLetterEditorState, action: Action): CoverLetterEditorState {
    switch (action.type) {
        case 'LOAD_VERSION':
            return {
                ...state,
                coverLetterId: action.payload.coverLetterId,
                currentVersion: action.payload.version,
                history: { past: [], future: [] },
                isDirty: false,
                isGenerating: false
            };

        case 'UPDATE_LOCAL': {
            if (!state.currentVersion) return state;

            const newPast = [...state.history.past, state.currentVersion];
            if (newPast.length > 50) newPast.shift();

            const newVersion: CoverLetterVersion = {
                ...state.currentVersion,
                content: action.payload.content,
                // version_id kept same for local edits until saved/improved
            };

            return {
                ...state,
                currentVersion: newVersion,
                history: {
                    past: newPast,
                    future: []
                },
                isDirty: true
            };
        }

        case 'UNDO': {
            if (state.history.past.length === 0 || !state.currentVersion) return state;

            const previous = state.history.past[state.history.past.length - 1];
            const newPast = state.history.past.slice(0, -1);
            const newFuture = [state.currentVersion, ...state.history.future];

            return {
                ...state,
                currentVersion: previous,
                history: {
                    past: newPast,
                    future: newFuture
                },
                isDirty: true
            };
        }

        case 'REDO': {
            if (state.history.future.length === 0 || !state.currentVersion) return state;

            const next = state.history.future[0];
            const newFuture = state.history.future.slice(1);
            const newPast = [...state.history.past, state.currentVersion];

            return {
                ...state,
                currentVersion: next,
                history: {
                    past: newPast,
                    future: newFuture
                },
                isDirty: true
            };
        }

        case 'GENERATE_START':
            return { ...state, isGenerating: true };

        case 'GENERATE_SUCCESS':
            return {
                ...state,
                isGenerating: false,
                isDirty: false,
                currentVersion: action.payload.version,
                history: {
                    past: [...state.history.past, state.currentVersion!], // Save previous state to history
                    future: []
                }
            };

        case 'GENERATE_ERROR':
            return { ...state, isGenerating: false };

        default:
            return state;
    }
}

// --- Context ---
interface CoverLetterContextType extends CoverLetterEditorState {
    loadCoverLetter: (coverLetterId: string) => Promise<void>;
    updateContent: (content: CoverLetterContent) => void;
    improveWithAI: (instruction: string, targetPath?: string) => Promise<void>;
    undo: () => void;
    redo: () => void;
    canUndo: boolean;
    canRedo: boolean;
}

const CoverLetterContext = createContext<CoverLetterContextType | undefined>(undefined);

export function CoverLetterProvider({ children }: { children: ReactNode }) {
    const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
    const canvasRef = React.useRef<HTMLDivElement>(null);

    const loadCoverLetter = useCallback(async (coverLetterId: string) => {
        try {
            const version = await coverLetterService.getLatestVersion(coverLetterId);
            if (version) {
                dispatch({ type: 'LOAD_VERSION', payload: { version, coverLetterId } });
            } else {
                toast.error("Cover letter not found");
            }
        } catch (e) {
            console.error(e);
            toast.error("Failed to load cover letter");
        }
    }, []);

    const updateContent = useCallback((content: CoverLetterContent) => {
        dispatch({ type: 'UPDATE_LOCAL', payload: { content } });
    }, []);

    const improveWithAI = useCallback(async (instruction: string, targetPath?: string) => {
        if (!state.coverLetterId) return;

        dispatch({ type: 'GENERATE_START' });
        try {
            const newVersion = await coverLetterService.improveCoverLetter(state.coverLetterId, {
                instruction,
                target_path: targetPath
            });
            dispatch({ type: 'GENERATE_SUCCESS', payload: { version: newVersion } });
            toast.success("Cover letter improved!");
        } catch (e) {
            console.error(e);
            dispatch({ type: 'GENERATE_ERROR' });
            toast.error("Failed to improve cover letter");
        }
    }, [state.coverLetterId]);

    const undo = useCallback(() => dispatch({ type: 'UNDO' }), []);
    const redo = useCallback(() => dispatch({ type: 'REDO' }), []);

    return (
        <CoverLetterContext.Provider value={{
            ...state,
            loadCoverLetter,
            updateContent,
            improveWithAI,
            undo,
            redo,
            canvasRef,
            canUndo: state.history.past.length > 0,
            canRedo: state.history.future.length > 0
        }}>
            {children}
        </CoverLetterContext.Provider>
    );
}

export function useCoverLetterEditor() {
    const context = useContext(CoverLetterContext);
    if (!context) throw new Error("useCoverLetterEditor must be used within CoverLetterProvider");
    return context;
}
